import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BookOpen, FileText, MessageSquare, Upload, CheckCircle2, XCircle, Clock, Award, Video, Calendar, User as UserIcon, ExternalLink, PlayCircle, FileCheck2, Send, X } from "lucide-react";
import ClaimAccountModal from "@/components/ClaimAccountModal";

export default function StudentDashboard() {
  const { user, refreshUser } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [now, setNow] = useState(new Date());
  const [uploading, setUploading] = useState(false);
  const [docName, setDocName] = useState("");
  const [file, setFile] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [dc3Modal, setDc3Modal] = useState(null); // enrollment object
  const [dc3Sending, setDc3Sending] = useState(false);
  const [dc3Msg, setDc3Msg] = useState("");
  const [claimModal, setClaimModal] = useState(null); // { message }

  const load = async () => {
    const [e, d, t, cl] = await Promise.all([
      api.get("/enrollments"),
      api.get("/documents/my"),
      api.get("/tickets"),
      api.get("/classrooms/my"),
    ]);
    setEnrollments(e.data);
    setDocuments(d.data);
    setTickets(t.data);
    setClassrooms(cl.data);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const uploadDoc = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    if (!file || !docName) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("document_name", docName);
    setUploading(true);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg("Documento subido, en espera de validación");
      setDocName(""); setFile(null);
      const input = document.getElementById("doc-file-input"); if (input) input.value = "";
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setUploading(false); }
  };

  const downloadCert = async (courseId, title) => {
    try {
      const resp = await api.get(`/certificate/${courseId}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `certificado-${title}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // Blob 403 → need to parse the JSON error out of the blob
      let detail = e.response?.data;
      if (detail instanceof Blob) {
        try { detail = JSON.parse(await detail.text())?.detail; } catch { detail = null; }
      } else {
        detail = detail?.detail;
      }
      if (detail && typeof detail === "object" && detail.code === "claim_required") {
        setClaimModal({ message: detail.message });
      } else {
        setErr(formatApiError(detail) || e.message);
      }
    }
  };

  const requestDC3 = async () => {
    if (!dc3Modal) return;
    setDc3Sending(true); setDc3Msg("");
    try {
      await api.post(`/certificate/${dc3Modal.id}/request-dc3`);
      setDc3Msg("¡Solicitud enviada! Recibirás un correo de confirmación.");
      load();
      setTimeout(() => { setDc3Modal(null); setDc3Msg(""); }, 2200);
    } catch (e) {
      setDc3Msg(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setDc3Sending(false); }
  };

  const statusBadge = {
    pending: <span className="inline-flex items-center gap-1 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full"><Clock className="w-3 h-3" /> Pendiente</span>,
    approved: <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full"><CheckCircle2 className="w-3 h-3" /> Aprobado</span>,
    rejected: <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full"><XCircle className="w-3 h-3" /> Rechazado</span>,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      <div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-marino tracking-tight">Hola, {user?.name}</h1>
        <p className="text-marino/70 mt-1">Este es tu panel de estudiante. Continúa aprendiendo.</p>
      </div>

      {/* Salón / Clase virtual */}
      {classrooms.length > 0 && (
        <section data-testid="classroom-section">
          <h2 className="font-heading font-bold text-marino text-xl sm:text-2xl flex items-center gap-2 mb-4">
            <Video className="w-6 h-6 text-naranja" /> Clase virtual · En vivo
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {classrooms.map((cl) => {
              const hasMeeting = cl.meeting_date && cl.meeting_start;
              let state = "no_meeting"; // no_meeting | upcoming | live | ended
              let startAt = null; let endAt = null;
              if (hasMeeting) {
                startAt = new Date(`${cl.meeting_date}T${cl.meeting_start}:00`);
                endAt = cl.meeting_end ? new Date(`${cl.meeting_date}T${cl.meeting_end}:00`) : new Date(startAt.getTime() + 90 * 60000);
                const opensAt = new Date(startAt.getTime() - 15 * 60000);
                if (now >= opensAt && now <= endAt) state = "live";
                else if (now < opensAt) state = "upcoming";
                else state = "ended";
              }
              const canJoin = state === "live" && cl.meeting_url;
              return (
                <div key={cl.id} data-testid={`classroom-${cl.id}`} className="bg-gradient-to-br from-marino to-marino/90 text-white rounded-2xl p-5 shadow-lg overflow-hidden relative">
                  <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-naranja/10 blur-2xl" />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${state === "live" ? "bg-green-500 text-white animate-pulse" : state === "upcoming" ? "bg-naranja text-white" : "bg-white/20 text-white/70"}`}>
                        {state === "live" ? "● En vivo" : state === "upcoming" ? "Próximamente" : state === "ended" ? "Finalizada" : "Sin agendar"}
                      </span>
                      <span className="text-xs text-white/60 truncate">{cl.name}</span>
                    </div>
                    {cl.description && <p className="text-white/70 text-sm mt-1 line-clamp-1">{cl.description}</p>}
                    {hasMeeting ? (
                      <div className="mt-4 space-y-2 text-sm">
                        <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-naranja" /> {cl.meeting_date}</div>
                        <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-naranja" /> {cl.meeting_start}{cl.meeting_end && ` – ${cl.meeting_end}`}</div>
                        {cl.teacher_name && <div className="flex items-center gap-2"><UserIcon className="w-4 h-4 text-naranja" /> Instructor {cl.teacher_name}</div>}
                      </div>
                    ) : (
                      <p className="mt-4 text-sm text-white/70">Aún no hay una sesión de Teams programada.</p>
                    )}

                    {hasMeeting && (
                      <div className="mt-5 flex flex-wrap gap-2">
                        {canJoin ? (
                          <a
                            data-testid={`join-meeting-${cl.id}`}
                            href={cl.meeting_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-naranja hover:bg-naranjaHover text-white px-5 py-2.5 rounded-lg font-semibold transition-colors"
                          >
                            <Video className="w-4 h-4" /> Ingresar a la sesión <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        ) : (
                          <button
                            disabled
                            data-testid={`join-disabled-${cl.id}`}
                            className="inline-flex items-center gap-2 bg-white/10 text-white/60 border border-white/20 px-5 py-2.5 rounded-lg font-semibold cursor-not-allowed"
                          >
                            {state === "upcoming" ? "Se habilita 15 min antes" : state === "ended" ? "Sesión finalizada" : "Ingresar a la sesión"}
                          </button>
                        )}
                        {state === "ended" && cl.recording_url && (
                          <a
                            data-testid={`recording-${cl.id}`}
                            href={cl.recording_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-white text-marino px-5 py-2.5 rounded-lg font-semibold hover:bg-white/90 transition-colors"
                          >
                            <PlayCircle className="w-4 h-4" /> Ver grabación <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    )}
                    {cl.courses?.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-white/10 text-xs text-white/70">
                        {cl.courses.length} curso{cl.courses.length !== 1 && "s"} asignado{cl.courses.length !== 1 && "s"} · matrícula automática
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Mis cursos */}
      <section>
        <h2 className="font-heading font-bold text-marino text-xl sm:text-2xl flex items-center gap-2 mb-4">
          <BookOpen className="w-6 h-6 text-naranja" /> Mis cursos
        </h2>
        {enrollments.length === 0 ? (
          <div className="bg-white border border-gris/30 rounded-xl p-8 text-center">
            <p className="text-marino/70">Aún no estás inscrito en ningún curso.</p>
            <Link to="/redeem" data-testid="empty-redeem-link" className="mt-3 inline-block bg-naranja text-white px-5 py-2 rounded-lg font-medium hover:bg-naranjaHover">Activar código</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {enrollments.map((e) => (
              <div key={e.id} data-testid={`enrollment-${e.course_id}`} className="bg-white border border-gris/30 rounded-xl overflow-hidden shadow-sm">
                {e.course?.thumbnail && <img src={e.course.thumbnail} className="w-full h-32 object-cover" alt="" />}
                <div className="p-5">
                  <h3 className="font-heading font-bold text-marino leading-snug">{e.course?.title}</h3>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-marino/70 mb-1">
                      <span>Progreso</span><span data-testid={`progress-${e.course_id}`}>{e.progress_percentage}%</span>
                    </div>
                    <div className="h-2 bg-gris/20 rounded-full overflow-hidden">
                      <div className="h-full bg-naranja transition-all" style={{ width: `${e.progress_percentage}%` }} />
                    </div>
                  </div>
                  <div className="mt-3 text-xs">
                    {e.status === "completed" && <span className="text-green-700 font-semibold">✓ Completado</span>}
                    {e.status === "failed" && <span className="text-red-600 font-semibold">✗ Reprobado</span>}
                    {e.status === "active" && <span className="text-marino/70">Activo</span>}
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Link to={`/learn/${e.course_id}`} data-testid={`continue-${e.course_id}`} className="flex-1 bg-naranja text-white text-center px-3 py-2 rounded-lg text-sm font-medium hover:bg-naranjaHover">
                      {e.status === "completed" ? "Repasar" : "Continuar"}
                    </Link>
                    {e.status === "completed" && (
                      <button data-testid={`cert-${e.course_id}`} onClick={() => downloadCert(e.course_id, e.course?.title)} title="Descargar certificado" className="bg-gris text-marino px-3 py-2 rounded-lg text-sm font-medium hover:bg-grisHover flex items-center gap-1">
                        <Award className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {e.status === "completed" && (
                    <button
                      data-testid={`request-dc3-${e.course_id}`}
                      onClick={() => { setDc3Modal(e); setDc3Msg(""); }}
                      disabled={!!e.dc3_requested_at}
                      className={`mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                        e.dc3_requested_at
                          ? "bg-green-50 text-green-700 border border-green-200 cursor-not-allowed"
                          : "bg-marino text-white hover:bg-marino/90"
                      }`}
                    >
                      {e.dc3_requested_at ? (
                        <><CheckCircle2 className="w-3.5 h-3.5" /> DC-3 solicitada</>
                      ) : (
                        <><FileCheck2 className="w-3.5 h-3.5" /> Solicitar Constancia DC-3</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Documentos y consultas grid */}
      <div className="grid lg:grid-cols-2 gap-8">
        <section>
          <h2 className="font-heading font-bold text-marino text-xl sm:text-2xl flex items-center gap-2 mb-4">
            <FileText className="w-6 h-6 text-naranja" /> Mis documentos
          </h2>
          <form onSubmit={uploadDoc} className="bg-white border border-gris/30 rounded-xl p-5 shadow-sm space-y-3">
            <input
              data-testid="doc-name-input"
              value={docName} onChange={(e) => setDocName(e.target.value)} required
              placeholder="Nombre del documento (ej: DNI)"
              className="w-full px-3 py-2 border border-gris rounded-lg focus:border-marino focus:outline-none text-sm"
            />
            <input id="doc-file-input" data-testid="doc-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files[0])} required
              className="w-full text-sm" />
            <p className="text-xs text-marino/60">Formatos: PDF, JPG, PNG · Máx 5MB</p>
            {err && <div className="text-naranja text-sm">{err}</div>}
            {msg && <div className="text-green-700 text-sm">{msg}</div>}
            <button disabled={uploading} data-testid="upload-doc-btn" className="w-full bg-naranja text-white py-2 rounded-lg font-medium hover:bg-naranjaHover disabled:opacity-60">
              <Upload className="w-4 h-4 inline mr-1" /> {uploading ? "Subiendo..." : "Subir documento"}
            </button>
          </form>

          <div className="mt-4 space-y-2">
            {documents.map((d) => (
              <div key={d.id} data-testid={`doc-${d.id}`} className="bg-white border border-gris/30 rounded-lg p-3 flex items-center gap-3">
                <FileText className="w-5 h-5 text-marino/60" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-marino text-sm truncate">{d.document_name}</div>
                  <div className="text-xs text-marino/60 truncate">{d.original_filename}</div>
                  {d.status === "rejected" && d.rejection_reason && (
                    <div className="text-xs text-red-600 mt-1">Motivo: {d.rejection_reason}</div>
                  )}
                </div>
                {statusBadge[d.status]}
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading font-bold text-marino text-xl sm:text-2xl flex items-center gap-2 mb-4">
            <MessageSquare className="w-6 h-6 text-naranja" /> Buzón de consultas
          </h2>
          <div className="space-y-3">
            {tickets.length === 0 && <div className="bg-white border border-gris/30 rounded-xl p-6 text-center text-marino/60">No has enviado consultas aún.</div>}
            {tickets.map((t) => (
              <div key={t.id} data-testid={`ticket-${t.id}`} className="bg-white border border-gris/30 rounded-lg p-4">
                <div className="text-xs text-marino/60">{t.course_title} · {new Date(t.created_at).toLocaleDateString()}</div>
                <div className="mt-1 text-marino"><span className="font-semibold">P:</span> {t.question_text}</div>
                {t.answer_text ? (
                  <div className="mt-2 pl-3 border-l-2 border-naranja text-marino/80"><span className="font-semibold text-naranja">R:</span> {t.answer_text}</div>
                ) : (
                  <div className="mt-2 text-xs text-yellow-700">Pendiente de respuesta</div>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* DC-3 Request Modal */}
      {dc3Modal && (
        <div data-testid="dc3-modal" className="fixed inset-0 z-[80] bg-marino/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in">
            <div className="bg-marino text-white px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileCheck2 className="w-6 h-6 text-naranja" />
                <h3 className="font-heading font-bold text-lg">Solicitar Constancia DC-3</h3>
              </div>
              <button data-testid="dc3-modal-close" onClick={() => !dc3Sending && setDc3Modal(null)} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-marino/80 text-sm leading-relaxed">
                Estás a punto de solicitar tu <strong>Constancia DC-3</strong> firmada para el curso:
              </p>
              <div className="bg-marino/[0.04] border border-gris/30 rounded-lg p-3 text-sm">
                <div className="font-semibold text-marino">{dc3Modal.course?.title}</div>
                <div className="text-xs text-marino/60 mt-1">Calificación final: <strong>{dc3Modal.final_exam_score ?? 0}%</strong></div>
              </div>
              <p className="text-marino/70 text-xs leading-relaxed">
                Nuestro equipo administrativo procesará tu solicitud y te enviará la constancia firmada por correo. Recibirás una confirmación en tu email <strong>{user?.email}</strong>.
              </p>
              {dc3Msg && (
                <div className={`text-sm rounded-lg p-3 ${dc3Msg.startsWith("¡") ? "bg-green-50 text-green-700 border border-green-200" : "bg-naranja/10 text-naranja border border-naranja/30"}`}>
                  {dc3Msg}
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  data-testid="dc3-cancel"
                  onClick={() => setDc3Modal(null)}
                  disabled={dc3Sending}
                  className="flex-1 bg-gris text-marino px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-grisHover disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  data-testid="dc3-confirm"
                  onClick={requestDC3}
                  disabled={dc3Sending}
                  className="flex-1 bg-naranja text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-naranjaHover disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" /> {dc3Sending ? "Enviando..." : "Enviar solicitud"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {claimModal && (
        <ClaimAccountModal
          message={claimModal.message}
          onClose={() => setClaimModal(null)}
          onClaimed={async () => {
            setClaimModal(null);
            try { await refreshUser?.(); } catch { /* ignore */ }
            setMsg("Cuenta reclamada. Ya puedes descargar tus certificados.");
            setTimeout(() => setMsg(""), 3000);
          }}
        />
      )}
    </div>
  );
}
